import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const MEMORY_SIZE = 0x32a0
const CHANNEL_COUNT = 200
const CHANNEL_SIZE = 32
const STATUS_START = 0x1940
const NAME_START = 24

type AnyToneSpec = {
  id: string
  vendor: string
  model: string
  summary: string
  nameLength: 5 | 6
  radioType: string
}

export type AnyTone778Profile = {
  protocol: 'anytone778'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 200
  nameLength: 5 | 6
  memorySize: number
  radioType: string
  powerLevels: readonly string[]
}

const SPECS: readonly AnyToneSpec[] = [
  {
    id: 'anytone-778uv',
    vendor: 'AnyTone',
    model: '778UV',
    summary: 'AnyTone AT-778UV dual-band mobile.',
    nameLength: 5,
    radioType: 'AT778UV',
  },
  {
    id: 'retevis-rt95',
    vendor: 'Retevis',
    model: 'RT95',
    summary: 'Retevis RT95 dual-band mobile.',
    nameLength: 5,
    radioType: 'RT95',
  },
  {
    id: 'crt-micron-uv',
    vendor: 'CRT',
    model: 'Micron UV',
    summary: 'CRT Micron UV dual-band mobile.',
    nameLength: 5,
    radioType: 'MICRON',
  },
  {
    id: 'midland-dbr2500',
    vendor: 'Midland',
    model: 'DBR2500',
    summary: 'Midland DBR2500 dual-band mobile.',
    nameLength: 5,
    radioType: 'DBR2500',
  },
  {
    id: 'yedro-yc-m04vus',
    vendor: 'Yedro',
    model: 'YC-M04VUS',
    summary: 'Yedro YC-M04VUS dual-band mobile.',
    nameLength: 5,
    radioType: 'YCM04UV',
  },
  {
    id: 'anytone-778uv-vox',
    vendor: 'AnyTone',
    model: '778UV VOX',
    summary: 'AnyTone 778UV VOX dual-band mobile.',
    nameLength: 6,
    radioType: '778UV-P',
  },
  {
    id: 'retevis-rt95-vox',
    vendor: 'Retevis',
    model: 'RT95 VOX',
    summary: 'Retevis RT95 VOX dual-band mobile.',
    nameLength: 6,
    radioType: 'RT95-P',
  },
  {
    id: 'crt-micron-uv-v2',
    vendor: 'CRT',
    model: 'Micron UV V2',
    summary: 'CRT Micron UV V2 dual-band mobile.',
    nameLength: 6,
    radioType: 'MICRONP',
  },
]

export const ANYTONE_778_PROFILES: readonly AnyTone778Profile[] = SPECS.map(
  (spec) => ({
    protocol: 'anytone778',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      '200 channels',
      `${spec.nameLength}-character names`,
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: spec.nameLength,
    memorySize: MEMORY_SIZE,
    radioType: spec.radioType,
    powerLevels: ['Low', 'Medium', 'High'],
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = size - 1; index >= 0; index -= 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function statusBit(image: Uint8Array, start: number, number: number): boolean {
  const index = number - 1
  return (
    (image[start + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
  )
}

function setStatusBit(
  image: Uint8Array,
  start: number,
  number: number,
  value: boolean,
): void {
  const index = number - 1
  const offset = start + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function allDcsCode(index: number): number {
  return Number(index.toString(8).padStart(3, '0'))
}

function allDcsIndex(code: number): number {
  const text = code.toString()
  return /^[0-7]{3}$/.test(text) ? Number.parseInt(text, 8) : -1
}

function decodeTone(
  ctcssEnabled: boolean,
  ctcssValue: number,
  dtcsEnabled: boolean,
  dtcsLow: number,
  dtcsHigh: number,
  inverted: boolean,
): DecodedTone {
  if (dtcsEnabled) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: allDcsCode(dtcsLow | (dtcsHigh << 8)),
      polarity: inverted ? 'R' : 'N',
    }
  }
  if (ctcssEnabled) {
    return {
      mode: 'ctcss',
      ctcss: CTCSS_TONES[ctcssValue] ?? null,
      dtcs: null,
      polarity: 'N',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function applyTone(
  raw: Uint8Array,
  channel: WebChannel,
): void {
  raw[11] &= ~0xf0
  raw[12] = 0
  raw[13] = 0
  raw[14] = 0
  raw[15] &= 0x7f
  raw[16] = 0
  raw[17] &= 0x7f
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('That CTCSS tone is not supported by this radio.')
    raw[11] |= 0x90
    raw[20] |= 0x80
    raw[12] = index
    raw[13] = index
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = allDcsIndex(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    raw[11] |= 0x50
    raw[20] |= 0x80
    raw[14] = index & 0xff
    raw[16] = index & 0xff
    raw[15] |= (index >> 8) << 7
    raw[17] |= (index >> 8) << 7
    if (channel.dtcsPolarity === 'R') {
      raw[15] |= 0x40
      raw[17] |= 0x40
    }
  }
}

function decodeName(raw: Uint8Array, length: 5 | 6): string {
  const source = length === 5 ? raw.slice(1, 6) : raw.slice(0, 6)
  return String.fromCharCode(
    ...source.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

export function parseAnyTone778Channel(
  image: Uint8Array,
  profile: AnyTone778Profile,
  number: number,
): WebChannel {
  if (!statusBit(image, STATUS_START, number)) return emptyChannel(number)
  const offset = (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxFrequency = decodeBcd(raw.slice(0, 4)) * 10
  if (!rxFrequency) return emptyChannel(number)
  const shift = decodeBcd(raw.slice(4, 8)) * 10
  const duplex = (raw[9] >> 6) & 0x03
  const txFrequency =
    raw[10] & 0x80
      ? null
      : duplex === 1
        ? rxFrequency + shift
        : duplex === 2
          ? rxFrequency - shift
          : duplex === 3
            ? shift
            : rxFrequency
  const txTone = decodeTone(
    (raw[11] & 0x80) !== 0,
    raw[13],
    (raw[11] & 0x40) !== 0,
    raw[16],
    raw[17] >> 7,
    (raw[17] & 0x40) !== 0,
  )
  const rxtone = decodeTone(
    (raw[11] & 0x20) !== 0,
    raw[12],
    (raw[11] & 0x10) !== 0,
    raw[14],
    raw[15] >> 7,
    (raw[15] & 0x40) !== 0,
  )
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(NAME_START, NAME_START + 6), profile.nameLength),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: (raw[10] >> 4) & 0x03 ? 'FM' : 'NFM',
    power: profile.powerLevels[(raw[9] >> 4) & 0x03] ?? 'Low',
    skip: !statusBit(image, STATUS_START + 32, number),
  }
}

export function parseAnyTone778Channels(
  image: Uint8Array,
  profile: AnyTone778Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, index) => parseAnyTone778Channel(image, profile, index + 1),
  )
}

export function applyAnyTone778Channel(
  image: Uint8Array,
  profile: AnyTone778Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error('Channel must be between 1 and 200.')
  }
  setStatusBit(image, STATUS_START, channel.number, !channel.empty)
  setStatusBit(
    image,
    STATUS_START + 32,
    channel.number,
    !channel.empty && !channel.skip,
  )
  const offset = (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  raw.set(encodeBcd(Math.round(channel.rxFrequency / 10), 4), 0)
  const tx = channel.txFrequency
  let duplex = 0
  let shift = 0
  if (!tx) {
    raw[10] |= 0x80
  } else {
    const delta = tx - channel.rxFrequency
    if (delta > 0) {
      duplex = 1
      shift = delta
    } else if (delta < 0) {
      duplex = 2
      shift = -delta
    }
  }
  raw.set(encodeBcd(Math.round(shift / 10), 4), 4)
  raw[9] =
    ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 4) |
    ((duplex & 0x03) << 6)
  raw[10] = (raw[10] & 0x80) | ((channel.mode === 'FM' ? 2 : 0) << 4)
  applyTone(raw, channel)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, profile.nameLength)
    .padEnd(profile.nameLength, ' ')
  const start = profile.nameLength === 5 ? 1 : 0
  for (let index = 0; index < profile.nameLength; index += 1) {
    raw[NAME_START + start + index] = name.charCodeAt(index)
  }
}

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

async function sendCommand(
  transport: RadioTransport,
  command: Uint8Array,
  responseLength: number,
  timeout = 3000,
): Promise<Uint8Array> {
  await transport.write(command)
  await transport.readExact(command.length, timeout)
  return transport.readExact(responseLength, timeout)
}

async function enterProgrammingMode(
  profile: AnyTone778Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  const entered = await sendCommand(
    transport,
    new TextEncoder().encode('PROGRAM'),
    3,
  )
  if (entered[0] !== 0x51 || entered[1] !== 0x58 || entered[2] !== ACK) {
    throw new Error('The radio refused AnyTone programming mode.')
  }
  const version = await sendCommand(transport, Uint8Array.of(0x02), 16)
  const model = String.fromCharCode(
    ...version.slice(1, 8).filter((value) => value !== 0),
  ).trim()
  if (version[0] !== 0x49 || version[15] !== ACK || model !== profile.radioType) {
    throw new Error(
      `The connected radio identifies as ${model || 'unknown'}, not ${profile.model}.`,
    )
  }
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
  const response = await sendCommand(transport, command, BLOCK_SIZE + 6, 4000)
  if (
    response[3] !== BLOCK_SIZE ||
    response[BLOCK_SIZE + 5] !== ACK ||
    checksum(response.slice(1, BLOCK_SIZE + 4)) !== response[BLOCK_SIZE + 4]
  ) {
    throw new Error(`Invalid AnyTone read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4, 4 + BLOCK_SIZE)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + BLOCK_SIZE + 2)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(data, 4)
  frame[BLOCK_SIZE + 4] = checksum(frame.slice(1, BLOCK_SIZE + 4))
  frame[BLOCK_SIZE + 5] = ACK
  const response = await sendCommand(transport, frame, 1)
  if (response[0] !== ACK) {
    throw new Error(`The radio rejected write block 0x${address.toString(16)}.`)
  }
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  try {
    await sendCommand(transport, new TextEncoder().encode('END'), 0, 500)
  } catch {
    // Some firmware revisions close without replying to END.
  }
}

export async function readAnyTone778RadioWithTransport(
  profile: AnyTone778Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading AnyTone 778UV memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeAnyTone778RadioWithTransport(
  profile: AnyTone778Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than AnyTone 778UV memory.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    await readBlock(transport, 0x3b10)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing AnyTone 778UV memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
